import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import PandaWatermark from '@/assets/icons/cover-images/panda-watermark.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          async () => {
            const holderInner = document.createElement('div');
            holderInner.classList.add('holder-inner-var17');

            // Wait for font to load before measuring text
            await document.fonts.ready;

            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var17');

            const leftImage = new Image();
            leftImage.src = variation.leftImageSrc;
            leftImage.classList.add('image-var17');

            const rightImage = new Image();
            rightImage.src = variation.rightImageSrc;
            rightImage.classList.add('image-var17');

            const leftDiv = document.createElement('div');
            leftDiv.classList.add('left-div-var17');

            const rightDiv = document.createElement('div');
            rightDiv.classList.add('right-div-var17');

            leftDiv.appendChild(leftImage);

            rightDiv.appendChild(rightImage);

            if (variation.topText) {
              const topText = document.createElement('div');
              topText.classList.add('top-text-var17');
              let text = variation.topText;
              if (variation.topTextBrakes) {
                topText.innerText = text;
              } else {
                topText.innerHTML = text;
              }
              holderInner.appendChild(topText);
            }

            if (variation.bottomText) {
              const bottomText = document.createElement('div');
              const bottomTextP = document.createElement('p');
              bottomText.classList.add('bottom-text-var17');
              let text = `“${replaceQuotes(variation.bottomText)}”`;
              if (variation.bottomTextBrakes) {
                bottomTextP.innerText = text;
              } else {
                text = distributeTextCanvas(text, {
                  maxWidth: 980,
                  fontFamily: '"CustomArial", Arial',
                  minFontSize:
                    variation.bottomFontSize !== 0
                      ? variation.bottomFontSize!
                      : 38,
                });
                bottomTextP.innerHTML = text;
              }
              bottomText.appendChild(bottomTextP);
              imageHolder.appendChild(bottomText);

              if (variation.bottomFontSize) {
                bottomText.style.fontSize = `${variation.bottomFontSize}px`;
              }
            }

            const leftActualPhoto = createWatermarkTagImage(
              'left-actual-photo-var17',
              variation.leftWatermarkTag,
              variation.leftWatermarkPosition,
            );
            if (leftActualPhoto) {
              leftDiv.appendChild(leftActualPhoto);
            }

            const rightActualPhoto = createWatermarkTagImage(
              'right-actual-photo-var17',
              variation.rightWatermarkTag,
              variation.rightWatermarkPosition,
            );
            if (rightActualPhoto) {
              rightDiv.appendChild(rightActualPhoto);
            }

            if (variation.divider !== 'none') {
              const middleBreak = document.createElement('div');

              middleBreak.classList.add('break-var17');

              if (variation.divider === 'black') {
                middleBreak.classList.add('break-var17--black-var17');
              }

              imageHolder.appendChild(middleBreak);
            }

            imageHolder.appendChild(leftDiv);
            imageHolder.appendChild(rightDiv);
            if (variation.watermark) {
              const pandaWatermark = new Image();
              pandaWatermark.classList.add('watermark-var17');
              pandaWatermark.src = PandaWatermark;
              imageHolder.appendChild(pandaWatermark);
            }
            holderInner.appendChild(imageHolder);
            return holderInner;
          },
          {
            holderClassName: 'generated-images-holder-var17',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      const validImages = await generateImagesInParallel(
        variations,
        generateImageVariation,
        (current) => setCounter(current),
      );

      setImages(validImages);
      setShowProgress(false);
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages);
      } else {
        await downloadImagesAsZip(images);
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
